import {
  Column,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from 'ag-grid-community'
import { formatSprintLabel } from '../../../../domain/entity/SprintEntity'
import {
  SprintDetail,
  SprintStatusDisplayOrder,
} from '../../../../lib/functions/sprint'
import {
  EXPANDED_STATUSES,
  COLLAPSED_STATUSES,
  groupByStatus,
} from '../components/SprintAssignmentDialog/types'
import {
  getMenuIconHtml,
  ContextMenuItemId,
} from '../../../containers/commons/AgGrid/lib/contextMenu'
import {
  isProductBacklogItemRow,
  ProductBacklogItemRow,
  isAssociatedWithSomeSprintBySpi,
  isUnsetSprintRowUuid,
  UNSET_SPRINT_ROW,
} from '../productBacklog'
import {
  allDescendants,
  getSelectedNode,
} from '../../../containers/BulkSheetView/lib/gridApi'
import { intl } from '../../../../i18n'
import { SetSprint } from '../hooks/sprintManager'
import store from '../../../../store'
import { openKanban } from '../../Kanban/Kanban'
import { StatusKanbanSearchCondition } from '../../Kanban/hooks/useSearchCondition'

// 展開・折りたたみ後にセル選択状態を復元する
// setTimeout(fn, 0)で次のイベントループに遅延させ、AG-GridのDOM更新完了後に実行する
const reselectNodes = (api: GridApi, nodeIds: string[], columns: Column[]) => {
  setTimeout(() => {
    api.clearCellSelection()
    nodeIds.forEach(id => {
      const node = api.getRowNode(id)
      if (node?.rowIndex != null) {
        api.addCellRange({
          rowStartIndex: node.rowIndex,
          rowEndIndex: node.rowIndex,
          columns,
        })
      }
    })
  }, 0)
}

const processExpandableNodes = (
  params: GetContextMenuItemsParams,
  processNode: (node: IRowNode) => void
) => {
  const selectedNodes = getSelectedNode(params.api)
  const columns = params.api.getCellRanges()?.[0]?.columns
  const expandableNodeIds = selectedNodes
    .filter(node => node.isExpandable())
    .map(node => node.id!)

  selectedNodes.forEach(processNode)

  if (columns) {
    reselectNodes(params.api, expandableNodeIds, columns)
  }
}

export const expandSelectedRows = (
  params: GetContextMenuItemsParams
): MenuItemDef => {
  return {
    name: intl.formatMessage({
      id: 'bulksheet.contextMenu.expand.allLeafChildren',
    }),
    icon: getMenuIconHtml(ContextMenuItemId.EXPAND_ALL),
    action: () => {
      processExpandableNodes(params, node => {
        // 親ノードを先に展開してから子ノードを展開する
        if (node.isExpandable() && !node.expanded) {
          node.setExpanded(true)
        }
        allDescendants(node)
          .filter(child => child.isExpandable() && !child.expanded)
          .forEach(child => child.setExpanded(true))
      })
    },
  }
}

export const collapseSelectedRows = (
  params: GetContextMenuItemsParams
): MenuItemDef => {
  return {
    name: intl.formatMessage({
      id: 'bulksheet.contextMenu.collapse.allLeafChildren',
    }),
    icon: getMenuIconHtml(ContextMenuItemId.COLLAPSE_ALL),
    action: () => {
      processExpandableNodes(params, node => {
        // 子ノードを先に折りたたんでから親ノードを折りたたむ
        allDescendants(node)
          .filter(child => child.isExpandable() && child.expanded)
          .forEach(child => child.setExpanded(false))
        if (node.isExpandable() && node.expanded) {
          node.setExpanded(false)
        }
      })
    },
  }
}

export const setSprintMenuItems = (
  teamUuid: string | undefined,
  sprints: SprintDetail[],
  setSprint: SetSprint,
  params: GetContextMenuItemsParams<ProductBacklogItemRow>
): MenuItemDef => {
  // 選択されている行の親スプリントUUIDを取得
  const selectedPbiRows = getSelectedNode(params.api)
    .filter(v => Boolean(v.data) && isProductBacklogItemRow(v.data))
    .map(v => v.data)
  const currentSprintUuid =
    selectedPbiRows.length > 0 ? selectedPbiRows[0].treeValue[0] : undefined

  // 現在のスプリントだけを除外（同一スプリントへの移動は意味がないため）
  const excludeSprintUuids = currentSprintUuid
    ? new Set([currentSprintUuid])
    : undefined

  return {
    name: intl.formatMessage({ id: 'productBacklog.setSprint' }),
    icon: getMenuIconHtml(ContextMenuItemId.ADD_ROW),
    subMenu: generateSprintsSubMenu(
      teamUuid,
      sprints,
      (sprintUuid: string, pbiRows: ProductBacklogItemRow[]) =>
        setSprint(sprintUuid, pbiRows, currentSprintUuid),
      params,
      excludeSprintUuids
    ),
  }
}

export const removeFromSprintMenuItems = (
  removeFromParentSprintRow: (pbiRows: ProductBacklogItemRow[]) => void,
  params: GetContextMenuItemsParams<ProductBacklogItemRow>
): MenuItemDef | null => {
  const selectedPbiRows = getSelectedNode(params.api)
    .filter(v => Boolean(v.data) && isProductBacklogItemRow(v.data))
    .map(v => v.data)

  // スプリント未設定の行が含まれている場合は非表示
  const hasUnsetSprint = selectedPbiRows.some(
    row =>
      !isAssociatedWithSomeSprintBySpi(row) ||
      isUnsetSprintRowUuid(row.treeValue?.[0])
  )

  if (hasUnsetSprint) {
    return null
  }

  return {
    name: intl.formatMessage({ id: 'productBacklog.removeFromThisSprint' }),
    icon: getMenuIconHtml(ContextMenuItemId.REMOVE_ROW),
    action: () => {
      removeFromParentSprintRow(selectedPbiRows)
    },
  }
}

export const addSprintMenuItem = (
  teamUuid: string | undefined,
  sprints: SprintDetail[],
  addSprint: (sprintUuid: string, pbiRows: ProductBacklogItemRow[]) => void,
  params: GetContextMenuItemsParams<ProductBacklogItemRow>
): MenuItemDef | null => {
  const selectedPbiRows = getSelectedNode(params.api)
    .filter(v => Boolean(v.data) && isProductBacklogItemRow(v.data))
    .map(v => v.data)

  // implicit未設定（SPIが0件）の行が含まれている場合は非表示
  // explicit未設定（null-sprint SPIあり）の行は許可
  const hasImplicitUnsetSprint = selectedPbiRows.some(
    row => !isAssociatedWithSomeSprintBySpi(row)
  )

  if (hasImplicitUnsetSprint) {
    return null
  }

  // 現在のスプリントのみ除外（既に割り当てられているスプリントにも行を追加可能）
  const currentSprintUuid =
    selectedPbiRows.length > 0 ? selectedPbiRows[0].treeValue[0] : undefined
  const excludeSprintUuids = currentSprintUuid
    ? new Set([currentSprintUuid])
    : undefined

  return {
    name: intl.formatMessage({ id: 'productBacklog.addSprint' }),
    icon: getMenuIconHtml(ContextMenuItemId.ADD_MULTIPLE_ROW),
    subMenu: generateSprintsSubMenu(
      teamUuid,
      sprints,
      addSprint,
      params,
      excludeSprintUuids
    ),
  }
}

export const sprintInteractiveAdjustmentMenuItem = (
  params: GetContextMenuItemsParams<ProductBacklogItemRow>,
  openDialog: (row: ProductBacklogItemRow) => void
): MenuItemDef | null => {
  const selectedPbiRows = getSelectedNode(params.api)
    .filter(v => Boolean(v.data) && isProductBacklogItemRow(v.data))
    .map(v => v.data as ProductBacklogItemRow)

  // ProductBacklogItem行が選択されていない場合は非表示
  if (selectedPbiRows.length === 0) return null

  // 複数選択時は非活性（disabled）
  const isDisabled = selectedPbiRows.length !== 1

  return {
    name: intl.formatMessage({
      id: 'productBacklog.sprintInteractiveAdjustment.title',
    }),
    icon: getMenuIconHtml(ContextMenuItemId.EDIT),
    disabled: isDisabled,
    action: () => {
      if (!isDisabled) openDialog(selectedPbiRows[0])
    },
  }
}

const generateSprintsSubMenu = (
  teamUuid: string | undefined,
  sprints: SprintDetail[],
  callback: (sprintUuid: string, pbiRows: ProductBacklogItemRow[]) => void,
  params: GetContextMenuItemsParams<ProductBacklogItemRow>,
  excludeSprintUuids?: Set<string>
): (MenuItemDef | string)[] => {
  const filteredSprints = sprints
    .filter(
      sprint =>
        sprint.teamUuid === teamUuid &&
        (!excludeSprintUuids || !excludeSprintUuids.has(sprint.uuid))
    )
    .sort((a, b) => {
      const statusDiff =
        SprintStatusDisplayOrder.indexOf(a.status) -
        SprintStatusDisplayOrder.indexOf(b.status)
      if (statusDiff !== 0) return statusDiff
      return a.num - b.num
    })

  const { groups: statusGroups } = groupByStatus(filteredSprints)

  const createSprintAction = (sprintUuid: string) => () => {
    const selectedRows = getSelectedNode(params.api).filter(
      v => Boolean(v.data) && isProductBacklogItemRow(v.data)
    )
    callback(
      sprintUuid,
      selectedRows.map(row => row.data)
    )
  }

  const createSprintMenuItem = (option: SprintDetail): MenuItemDef => ({
    name: formatSprintLabel(option.name, option.startDate, option.endDate),
    action: createSprintAction(option.uuid),
  })

  const menuItems: (MenuItemDef | string)[] = []

  // INPROGRESS → STANDBY: ステータスヘッダー + スプリント一覧をフラットに
  for (const status of EXPANDED_STATUSES) {
    const group = statusGroups.get(status)
    if (!group) continue
    if (menuItems.length > 0) menuItems.push('separator')
    menuItems.push({ name: status, disabled: true })
    group.forEach(option =>
      menuItems.push({
        ...createSprintMenuItem(option),
        cssClasses: ['ag-menu-option-indent'],
      })
    )
  }

  // スプリント未設定
  if (!excludeSprintUuids || !excludeSprintUuids.has(UNSET_SPRINT_ROW.uuid)) {
    if (menuItems.length > 0) menuItems.push('separator')
    menuItems.push({
      name: intl.formatMessage({ id: 'productBacklog.unsetSprint' }),
      action: createSprintAction(UNSET_SPRINT_ROW.uuid),
    })
  }

  // FINISHED → CANCELED: サブメニュー（折りたたみ）
  for (const status of COLLAPSED_STATUSES) {
    const group = statusGroups.get(status)
    if (!group) continue
    if (menuItems.length > 0) menuItems.push('separator')
    menuItems.push({
      name: status,
      subMenu: group.map(createSprintMenuItem),
    })
  }

  return menuItems
}

export const openKanbanMenuItem = (
  params: GetContextMenuItemsParams<ProductBacklogItemRow>
): MenuItemDef | null => {
  const selectedNodes = getSelectedNode(params.api)
  const selectedPbiRows = selectedNodes
    .filter(v => Boolean(v.data) && isProductBacklogItemRow(v.data))
    .map(v => v.data as ProductBacklogItemRow)

  if (selectedPbiRows.length !== 1) return null

  const wbsItem = selectedPbiRows[0].wbsItem
  if (!wbsItem) return null

  return {
    name: intl.formatMessage({ id: 'kanban' }),
    icon: getMenuIconHtml(ContextMenuItemId.OPEN_KANBAN),
    action: () => {
      const projectCode = store.getState().project.current?.code
      if (!projectCode) return
      const query: Partial<StatusKanbanSearchCondition> = {
        rootUuid: wbsItem.uuid,
      }
      openKanban(projectCode, query)
    },
  }
}
